<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import Toast from "primevue/toast";
import Button from "primevue/button";
import Tag from "primevue/tag";
import { fetchMe } from "./api";
import { pendingRouteToRestore } from "./deeplink";

const router = useRouter();
const email = ref<string | null>(null);
const isManager = ref(false);
const ready = ref(false);

// Where the user was headed before we bounced them to the login screen.
// The OIDC round-trip ends at a plain `GET /` (the backend can't see the hash
// fragment, so it can't redirect any deeper), which would otherwise drop a
// deep link from an email on the floor. `sessionStorage` survives that full
// page load and is scoped to the tab.
const PENDING_ROUTE_KEY = "ql:pending-route";

function rememberPendingRoute(): void {
  try {
    sessionStorage.setItem(PENDING_ROUTE_KEY, router.currentRoute.value.fullPath);
  } catch {
    // Storage disabled/full — the user just lands on the dashboard.
  }
}

function restorePendingRoute(): void {
  let pending: string | null = null;
  try {
    pending = sessionStorage.getItem(PENDING_ROUTE_KEY);
    sessionStorage.removeItem(PENDING_ROUTE_KEY);
  } catch {
    return;
  }
  const target = pendingRouteToRestore(pending, router.currentRoute.value.fullPath);
  if (target) router.replace(target);
}

onMounted(async () => {
  const me = await fetchMe();
  if (me) {
    email.value = me.email;
    isManager.value = me.is_manager;
    restorePendingRoute();
  } else if (router.currentRoute.value.name !== "login") {
    rememberPendingRoute();
    router.replace({ name: "login" });
  }
  ready.value = true;
});

async function logout(): Promise<void> {
  await fetch("/auth/logout", { method: "POST", credentials: "include" });
  email.value = null;
  router.replace({ name: "login" });
}
</script>

<template>
  <header class="topbar">
    <div class="brand">
      <i class="pi pi-shield"></i>
      <strong>Quality Ledger</strong>
    </div>
    <nav v-if="email && isManager" class="mainnav">
      <router-link :to="{ name: 'dashboard' }" class="navlink">Dashboard</router-link>
      <router-link
        :to="{ name: 'analytics' }"
        class="navlink"
        data-testid="nav-analytics"
      >
        Analytics
      </router-link>
    </nav>
    <div v-if="email" class="userbox">
      <span class="user-email">{{ email }}</span>
      <Tag
        :severity="isManager ? 'warn' : 'info'"
        :value="isManager ? 'manager' : 'contributor'"
      />
      <Button
        label="Sign out"
        icon="pi pi-sign-out"
        severity="secondary"
        text
        size="small"
        @click="logout"
      />
    </div>
  </header>
  <main>
    <router-view v-if="ready" :email="email" :is-manager="isManager" />
  </main>
  <Toast position="top-right" />
</template>

<style>
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 24px;
  background: var(--p-surface-900);
  color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.topbar .brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.userbox {
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 13px;
}
.user-email { opacity: 0.9; }
.mainnav {
  display: flex;
  gap: 4px;
  margin-right: auto;
  margin-left: 24px;
}
.navlink {
  color: white;
  opacity: 0.75;
  text-decoration: none;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 4px;
}
.navlink:hover { opacity: 1; }
.navlink.router-link-exact-active {
  opacity: 1;
  background: rgba(255, 255, 255, 0.12);
}
main {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
}
.topbar :deep(.p-button) {
  color: white;
}
</style>
